import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../app/typedReduxHooks'
import { establishEhrAuthenticatedContext } from '../../app/appSlice'
import { AuthenticatedContextGate } from '../authenticated-context/AuthenticatedContextGate'
import { invalidateAuthenticatedContext } from '../authenticated-context/authenticatedContextSlice'
import {
  clearAllAuthStorage,
  clearFailedEhrLaunchStorage,
  EHR_AUTH_PROVIDER,
  isEhrLaunchEnabled,
} from './ehrLaunchStorage'
import { EhrLaunchErrorPage } from './EhrLaunchErrorPage'

const getFragmentParams = () => new URLSearchParams(window.location.hash.replace(/^#/, ''))

export const EhrCompletePage = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [hasError, setHasError] = useState(false)
  const [identityEstablished, setIdentityEstablished] = useState(false)
  const contextStatus = useAppSelector((state) => state.authenticatedContext.status)
  const fragmentParams = useMemo(() => getFragmentParams(), [])
  const token = fragmentParams.get('token')
  const error = fragmentParams.get('error')
  const enabled = isEhrLaunchEnabled()

  useEffect(() => {
    const completeLaunch = async () => {
      window.history.replaceState(null, document.title, window.location.pathname)

      if (!enabled) {
        return
      }

      if (error || !token) {
        dispatch(invalidateAuthenticatedContext())
        // An explicit error means this launch failed, so any credentials it
        // left behind must be cleared. A missing fragment is not the same
        // case: it is what a revisit or refresh looks like after the launch
        // stripped its own hash, and the stored session may still be valid.
        if (error) clearFailedEhrLaunchStorage()
        setHasError(true)
        return
      }

      dispatch(invalidateAuthenticatedContext())
      clearAllAuthStorage()
      localStorage.setItem('auth_provider', EHR_AUTH_PROVIDER)
      localStorage.setItem('access_token', token)

      const establishmentResult = await dispatch(
        establishEhrAuthenticatedContext({ origin: 'fresh' })
      )
      if (establishEhrAuthenticatedContext.rejected.match(establishmentResult)) {
        clearFailedEhrLaunchStorage(token)
        setHasError(true)
        return
      }

      setIdentityEstablished(true)
    }

    completeLaunch()
  }, [dispatch, enabled, error, navigate, token])

  useEffect(() => {
    if (identityEstablished && contextStatus === 'ready') {
      navigate('/new-script', { replace: true })
    }
  }, [contextStatus, identityEstablished, navigate])

  if (!enabled) {
    return <EhrLaunchErrorPage title="EHR launch is unavailable" />
  }

  if (hasError) {
    return <EhrLaunchErrorPage title="EHR launch could not be completed" />
  }

  if (identityEstablished) {
    return <AuthenticatedContextGate>{null}</AuthenticatedContextGate>
  }

  return null
}
